import type { ClientDTO, TimelineReport } from '@brainkey/shared';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Users,
  Clock,
  Mail,
  Send,
  ArrowRight,
  Activity,
  Zap,
  FileCheck,
  MessageSquare,
  StickyNote,
  Wallet,
  Inbox,
  CalendarClock,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { PageHeader } from '../components/PageHeader';
import { Alert } from '../components/ui/Alert';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { staggerContainer, staggerItem } from '../components/ui/motionVariants';
import { SkeletonRow } from '../components/ui/Skeleton';
import { StatTile } from '../components/ui/StatTile';
import { TimelineRow } from '../components/ui/TimelineRow';
import type { ActivityKind } from '../lib/activity';
import {
  buildRecentActivity,
  countClientsInProgress,
  countPendingResponses,
  findReportsToSend,
} from '../lib/activity';
import { BOOKING_STATUS_TONE } from '../lib/bookingStatusTone';
import { endOfToday, startOfToday } from '../lib/dateRange';
import { errorMessage, formatDate, fullName } from '../lib/format';
import { ACTIVITY_AGGREGATE_WINDOW, useActivityAggregate } from '../lib/hooks/useActivityAggregate';
import { useCoachBookingsInRange } from '../lib/hooks/useBookings';
import { useClient, useClientsList } from '../lib/hooks/useClients';
import { useSendReport } from '../lib/hooks/useDelivery';
import { useLeadsList } from '../lib/hooks/useLeads';
import { usePaymentsList } from '../lib/hooks/usePayments';

function firstNameOf(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

const ACTIVITY_KIND_META: Record<
  ActivityKind,
  { icon: ComponentType<SVGProps<SVGSVGElement>>; tone: 'brand' | 'emerald' | 'amber' | 'zinc' }
> = {
  assessment: { icon: Activity, tone: 'amber' },
  report: { icon: FileCheck, tone: 'brand' },
  questionnaire: { icon: MessageSquare, tone: 'emerald' },
  delivery: { icon: Send, tone: 'zinc' },
  note: { icon: StickyNote, tone: 'zinc' },
};

const QUICK_ACTIONS = [
  { to: '/clients', title: 'Manage clients', desc: 'Create, search, and open client profiles' },
  {
    to: '/questionnaires',
    title: 'Questionnaires',
    desc: 'Dispatch and track questionnaire responses',
  },
  {
    to: '/reports',
    title: 'Review reports',
    desc: 'Browse and deliver generated coaching reports',
  },
  { to: '/bookings', title: 'Bookings', desc: 'View, reschedule, and cancel client sessions' },
  { to: '/templates', title: 'Report templates', desc: 'Manage versioned layouts and branding' },
];

function ReportToSendRow({
  client,
  report,
}: {
  client: ClientDTO;
  report: TimelineReport;
}): JSX.Element {
  const sendReport = useSendReport(client.id);

  return (
    <TimelineRow
      tone="brand"
      icon={<Mail className="w-5 h-5" />}
      href={`/clients/${client.id}`}
      title={fullName(client)}
      subtitle={`${report.variant} (${report.language}) · generated ${formatDate(report.generatedAt ?? report.createdAt)}`}
      trailing={
        <div className="flex flex-col items-end gap-2">
          <Button
            size="sm"
            variant="accent"
            loading={sendReport.isPending}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              sendReport.mutate({ reportId: report.id, body: {} });
            }}
            leadingIcon={<Send className="w-3 h-3" />}
          >
            Send
          </Button>
          {sendReport.isError && (
            <span className="text-[10px] text-rose-500 dark:text-rose-400">
              {errorMessage(sendReport.error, 'Could not send.')}
            </span>
          )}
          {sendReport.isSuccess && <Badge tone="success">Sent</Badge>}
        </div>
      }
    />
  );
}

function TodaySessionRow({
  clientId,
  startsAt,
  endsAt,
  status,
}: {
  clientId: string;
  startsAt: string;
  endsAt: string;
  status: string;
}): JSX.Element {
  const client = useClient(clientId);

  return (
    <TimelineRow
      tone="emerald"
      icon={<CalendarClock className="w-5 h-5" />}
      href={`/clients/${clientId}`}
      title={client.data ? fullName(client.data) : '…'}
      subtitle={`${formatTime(startsAt)} – ${formatTime(endsAt)}`}
      trailing={
        <Badge tone={BOOKING_STATUS_TONE[status] ?? 'neutral'} dot>
          {status}
        </Badge>
      }
    />
  );
}

export function DashboardPage(): JSX.Element {
  const { coach, hasPermission } = useAuth();
  // Cheap headline metric: total non-deleted clients (page 1, size 1 → total).
  const clients = useClientsList({ page: 1, pageSize: 1 });
  const aggregate = useActivityAggregate();

  const canViewPayments = hasPermission('payments:view');
  const canViewLeads = hasPermission('leads:view');

  // Cheap counts, same page-1/pageSize-1 trick as `clients` above — the
  // list total comes back regardless of page size.
  const pendingPayments = usePaymentsList({ status: 'SLIP_SUBMITTED', page: 1, pageSize: 1 });
  const newLeads = useLeadsList({ status: 'NEW', page: 1, pageSize: 1 });

  const todayFrom = startOfToday();
  const todayTo = endOfToday();
  const todayBookings = useCoachBookingsInRange(todayFrom.toISOString(), todayTo.toISOString());
  const todaySessions = [...(todayBookings.data ?? [])]
    .filter((b) => b.status === 'CONFIRMED')
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());

  // Only block on the client list itself — once it arrives, render whatever
  // partial data is ready and let it grow in as each client's timeline
  // resolves, instead of gating everything behind the slowest of N fetches.
  const awaitingClientList = aggregate.clients.length === 0 && aggregate.isLoading;

  const totalClients = clients.data?.total;
  const clientsInProgress = countClientsInProgress(aggregate.clients, aggregate.timelines);
  const pendingResponses = countPendingResponses(aggregate.timelines);
  const reportsToSend = findReportsToSend(aggregate.clients, aggregate.timelines);
  const recentActivity = buildRecentActivity(aggregate.clients, aggregate.timelines, 8);

  const statCards = [
    {
      to: undefined,
      label: 'Active clients',
      value: totalClients,
      hint: 'Total in your workspace',
      icon: Users,
      tone: 'brand' as const,
      loading: clients.isLoading,
    },
    {
      to: undefined,
      label: "Today's sessions",
      value: todaySessions.length,
      hint: 'Confirmed bookings today',
      icon: CalendarClock,
      tone: 'emerald' as const,
      loading: todayBookings.isLoading,
    },
    {
      to: undefined,
      label: 'Clients in progress',
      value: clientsInProgress,
      hint: 'Assessed, no delivered report yet',
      icon: Activity,
      tone: 'amber' as const,
      loading: awaitingClientList,
    },
    ...(canViewPayments
      ? [
          {
            to: '/payments',
            label: 'Payments to verify',
            value: pendingPayments.data?.total,
            hint: 'Bank slips awaiting review',
            icon: Wallet,
            tone: 'rose' as const,
            loading: pendingPayments.isLoading,
          },
        ]
      : []),
    ...(canViewLeads
      ? [
          {
            to: '/leads',
            label: 'New leads',
            value: newLeads.data?.total,
            hint: 'Contact-form inquiries',
            icon: Inbox,
            tone: 'amber' as const,
            loading: newLeads.isLoading,
          },
        ]
      : []),
  ];

  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={staggerContainer(0.1)}
      className="flex flex-col gap-8 pb-12"
    >
      <motion.div variants={staggerItem}>
        <PageHeader
          title={`Welcome back, ${coach ? firstNameOf(coach.name) : 'Coach'}`}
          description={`Here's what needs your attention — ${new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}.`}
        />
      </motion.div>

      <motion.div
        variants={staggerContainer(0.08)}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6"
      >
        {statCards.map((stat) => {
          const tile = (
            <StatTile
              label={stat.label}
              value={stat.value ?? '—'}
              hint={stat.hint}
              icon={stat.icon}
              tone={stat.tone}
              loading={stat.loading}
            />
          );
          return (
            <motion.div key={stat.label} variants={staggerItem}>
              {stat.to !== undefined ? <Link to={stat.to}>{tile}</Link> : tile}
            </motion.div>
          );
        })}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div variants={staggerItem} className="flex flex-col">
          <Card className="h-full flex flex-col">
            <CardHeader
              title="Reports to send"
              subtitle={`Based on your ${ACTIVITY_AGGREGATE_WINDOW} most recent clients`}
              icon={<Send className="w-5 h-5" />}
            />
            <CardBody className="flex-1 p-6">
              <AnimatePresence initial={false}>
                {aggregate.isError && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-4"
                  >
                    <Alert tone="danger">Could not load recent client activity.</Alert>
                  </motion.div>
                )}
              </AnimatePresence>
              {awaitingClientList ? (
                <div className="flex flex-col gap-3">
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </div>
              ) : reportsToSend.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center bg-zinc-50 dark:bg-zinc-900/40 rounded-2xl border border-zinc-100 dark:border-white/5 h-full min-h-[200px]">
                  <Mail className="w-8 h-8 text-zinc-300 dark:text-zinc-600 mb-3" />
                  <span className="text-zinc-500 dark:text-zinc-400 text-sm">
                    Nothing waiting to be sent.
                  </span>
                </div>
              ) : (
                <motion.div
                  initial="hidden"
                  animate="show"
                  variants={staggerContainer(0.06)}
                  className="flex flex-col gap-3"
                >
                  {reportsToSend.slice(0, 6).map(({ client, report }) => (
                    <ReportToSendRow key={report.id} client={client} report={report} />
                  ))}
                </motion.div>
              )}
            </CardBody>
          </Card>
        </motion.div>

        <motion.div variants={staggerItem} className="flex flex-col">
          <Card className="h-full flex flex-col">
            <CardHeader
              title="Today's bookings"
              subtitle={todayFrom.toLocaleDateString(undefined, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
              icon={<CalendarClock className="w-5 h-5" />}
              action={
                <Link
                  to="/bookings/today"
                  className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline whitespace-nowrap"
                >
                  View all
                </Link>
              }
            />
            <CardBody className="flex-1 p-6">
              {todayBookings.isLoading ? (
                <div className="flex flex-col gap-3">
                  <SkeletonRow />
                  <SkeletonRow />
                </div>
              ) : todaySessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center bg-zinc-50 dark:bg-zinc-900/40 rounded-2xl border border-zinc-100 dark:border-white/5 h-full min-h-[200px]">
                  <CalendarClock className="w-8 h-8 text-zinc-300 dark:text-zinc-600 mb-3" />
                  <span className="text-zinc-500 dark:text-zinc-400 text-sm">
                    No sessions scheduled today.
                  </span>
                </div>
              ) : (
                <motion.div
                  initial="hidden"
                  animate="show"
                  variants={staggerContainer(0.06)}
                  className="flex flex-col gap-3"
                >
                  {todaySessions.slice(0, 6).map((b) => (
                    <TodaySessionRow
                      key={b.id}
                      clientId={b.clientId}
                      startsAt={b.startsAt}
                      endsAt={b.endsAt}
                      status={b.status}
                    />
                  ))}
                </motion.div>
              )}
            </CardBody>
          </Card>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div variants={staggerItem}>
          <Card>
            <CardHeader
              title="Recent activity"
              subtitle={`Based on your ${ACTIVITY_AGGREGATE_WINDOW} most recent clients${pendingResponses > 0 ? ` · ${pendingResponses} response${pendingResponses === 1 ? '' : 's'} pending` : ''}`}
              icon={<Clock className="w-5 h-5" />}
            />
            <CardBody className="p-6">
              {awaitingClientList ? (
                <div className="flex flex-col gap-3">
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </div>
              ) : recentActivity.length === 0 ? (
                <div className="p-8 text-center text-sm text-zinc-500 bg-zinc-50 dark:bg-zinc-900/40 rounded-2xl border border-zinc-100 dark:border-white/5">
                  No activity yet.
                </div>
              ) : (
                <motion.div
                  initial="hidden"
                  animate="show"
                  variants={staggerContainer(0.06)}
                  className="flex flex-col gap-3"
                >
                  {recentActivity.map((entry) => {
                    const meta = ACTIVITY_KIND_META[entry.kind];
                    const Icon = meta.icon;
                    return (
                      <TimelineRow
                        key={`${entry.kind}-${entry.clientId}-${entry.at.toISOString()}`}
                        tone={meta.tone}
                        icon={<Icon className="w-4 h-4" />}
                        href={`/clients/${entry.clientId}`}
                        title={entry.label}
                        subtitle={entry.clientName}
                        trailing={
                          <span className="text-xs text-zinc-400 dark:text-zinc-500 tabular-nums">
                            {formatDate(entry.at.toISOString())}
                          </span>
                        }
                      />
                    );
                  })}
                </motion.div>
              )}
            </CardBody>
          </Card>
        </motion.div>

        <motion.div variants={staggerItem} className="flex flex-col">
          <Card className="h-full">
            <CardHeader
              title="Quick actions"
              subtitle="Jump into common workflows"
              icon={<Zap className="w-5 h-5" />}
            />
            <CardBody className="p-6">
              <motion.div
                initial="hidden"
                animate="show"
                variants={staggerContainer(0.06)}
                className="flex flex-col gap-3"
              >
                {QUICK_ACTIONS.map((action, i) => (
                  <motion.div key={i} variants={staggerItem}>
                    <Link
                      to={action.to}
                      className="group flex items-center justify-between p-5 bg-zinc-50 dark:bg-zinc-800/30 hover:bg-zinc-100 dark:hover:bg-zinc-800/70 rounded-2xl border border-zinc-100 dark:border-white/5 transition-all"
                    >
                      <div className="flex flex-col gap-1">
                        <span className="text-zinc-900 dark:text-zinc-100 font-medium group-hover:text-brand-600 dark:group-hover:text-brand-300 transition-colors">
                          {action.title}
                        </span>
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">
                          {action.desc}
                        </span>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-white dark:bg-zinc-950 flex items-center justify-center border border-zinc-200 dark:border-white/5 group-hover:border-brand-500/30 group-hover:shadow-soft dark:group-hover:shadow-glow-brand transition-all">
                        <ArrowRight className="w-5 h-5 text-zinc-400 dark:text-zinc-500 group-hover:text-brand-500 dark:group-hover:text-brand-400 transition-colors" />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </CardBody>
          </Card>
        </motion.div>
      </div>
    </motion.div>
  );
}
